import {useMemo} from 'react';
import {useLocation, useParams, useSearchParams} from 'react-router-dom';
import {
  CREATOR_STATUS,
  type CampaignCreatorRow,
  type CreatorStatus,
} from '@/views/campaigns/creators-tab/types/creatorTabTypes';
import useCampaignV2CreatorsQuery from '@/hooks/queries/use-campaign-v2-creators-query';
import useBrandId from '@/hooks/use-brand-id';

const VALID_STATUSES = Object.values(CREATOR_STATUS) as string[];

/** Hand-off shape callers stash on `location.state`. */
export interface CreatorCampaignRowNavState {
  campaignRow?: CampaignCreatorRow;
}

/**
 * Resolves the creator's row for a campaign-scoped profile URL.
 *
 * Prefers `location.state.campaignRow` (a trusted hand-off from the click
 * site, e.g. CampaignCreatorsTab) over a paginated-query fallback that only
 * sees creators on already-loaded pages — that fallback is best-effort
 * recovery for hard refreshes / deep links, not the primary data source.
 *
 * Returns null when neither source has a row.
 */
function useCreatorCampaignRow(
  profileId: string | undefined
): CampaignCreatorRow | null {
  const {campaignId} = useParams<{campaignId?: string}>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const statusParam = searchParams.get('creatorStatus');
  const brandId = useBrandId();

  const status: CreatorStatus | null =
    statusParam && VALID_STATUSES.includes(statusParam)
      ? (statusParam as CreatorStatus)
      : null;

  const enabled = !!campaignId && !!brandId && !!status && !!profileId;

  const {creators} = useCampaignV2CreatorsQuery({
    brandId,
    campaignId,
    status: status ?? CREATOR_STATUS.Applications,
    enabled,
  });

  const stateRow = (location.state as CreatorCampaignRowNavState | null)
    ?.campaignRow;

  return useMemo(() => {
    if (!campaignId || !profileId) return null;
    if (stateRow && stateRow.profileId === profileId) {
      return stateRow;
    }
    if (!enabled) return null;
    return creators.find((c) => c.profileId === profileId) ?? null;
  }, [campaignId, creators, enabled, profileId, stateRow]);
}

export default useCreatorCampaignRow;
